// HeatmapChart — matrix grid for recruit training module completion

import { useState } from "react";

export interface HeatmapCell {
  row: string;
  col: string;
  value: "complete" | "not_started" | "overdue";
  date?: string;
}

export interface HeatmapChartProps {
  cells: HeatmapCell[];
  rows: string[];
  cols: string[];
  isLoading?: boolean;
}

const CELL_CLASSES: Record<HeatmapCell["value"], string> = {
  complete: "bg-green-500/80 hover:bg-green-500 border-green-600/30",
  not_started: "bg-theme-overlay hover:bg-theme-raised border-theme-subtle",
  overdue: "bg-red-500/70 hover:bg-red-500 border-red-600/30",
};

const CELL_TITLE: Record<HeatmapCell["value"], string> = {
  complete: "Complete",
  not_started: "Not Started",
  overdue: "Overdue",
};

interface TooltipState {
  row: string;
  col: string;
  value: HeatmapCell["value"];
  date?: string;
  x: number;
  y: number;
}

export default function HeatmapChart({
  cells,
  rows,
  cols,
  isLoading = false,
}: HeatmapChartProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <div className="space-y-1" style={{ minWidth: 480 }}>
          {/* Header row skeleton */}
          <div className="flex gap-1 pl-32">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-8 h-14 rounded-sm shrink-0" />
            ))}
          </div>
          {/* Data rows skeleton */}
          {Array.from({ length: 5 }).map((_, r) => (
            <div key={r} className="flex items-center gap-1">
              <div className="skeleton h-4 w-28 rounded-md shrink-0" />
              {Array.from({ length: 6 }).map((_, c) => (
                <div key={c} className="skeleton w-8 h-8 rounded-sm shrink-0" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0 || cols.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">No training matrix data</p>
        <p className="text-xs text-theme-muted opacity-60">
          Recruit and module data will appear here after sync
        </p>
      </div>
    );
  }

  // Build a lookup map for O(1) cell access
  const cellMap = new Map<string, HeatmapCell>();
  for (const cell of cells) {
    cellMap.set(`${cell.row}::${cell.col}`, cell);
  }

  const ROW_LABEL_W = 128; // px
  const CELL_SIZE = 32; // px
  const COL_HEADER_H = 72; // px for rotated labels

  return (
    <div className="overflow-x-auto relative">
      <div
        style={{ minWidth: ROW_LABEL_W + cols.length * (CELL_SIZE + 4) + 16 }}
      >
        {/* Column headers — rotated 45deg */}
        <div
          className="flex"
          style={{ paddingLeft: ROW_LABEL_W, height: COL_HEADER_H }}
        >
          {cols.map((col) => (
            <div
              key={col}
              className="shrink-0 flex items-end justify-center"
              style={{ width: CELL_SIZE + 4 }}
            >
              <span
                className="text-xs font-medium text-theme-muted whitespace-nowrap origin-bottom-left"
                style={{
                  transform: "rotate(-45deg) translateX(-4px)",
                  display: "inline-block",
                  maxWidth: 80,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={col}
              >
                {col}
              </span>
            </div>
          ))}
        </div>

        {/* Data rows */}
        <div className="flex flex-col gap-1">
          {rows.map((row) => (
            <div key={row} className="flex items-center gap-1">
              {/* Row label — sticky left */}
              <div
                className="shrink-0 flex items-center"
                style={{ width: ROW_LABEL_W }}
              >
                <span
                  className="text-xs text-theme-secondary font-medium truncate"
                  style={{ maxWidth: ROW_LABEL_W - 8 }}
                  title={row}
                >
                  {row}
                </span>
              </div>

              {/* Cells */}
              {cols.map((col) => {
                const cell = cellMap.get(`${row}::${col}`);
                const cellValue: HeatmapCell["value"] =
                  cell?.value ?? "not_started";
                return (
                  <div
                    key={col}
                    className={`shrink-0 rounded-sm border cursor-default transition-colors ${CELL_CLASSES[cellValue]}`}
                    style={{ width: CELL_SIZE, height: CELL_SIZE }}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        row,
                        col,
                        value: cellValue,
                        date: cell?.date,
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                    aria-label={`${row} – ${col}: ${CELL_TITLE[cellValue]}${cell?.date ? ` (${cell.date})` : ""}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 glass rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="font-semibold text-theme-primary">{tooltip.row}</p>
          <p className="text-theme-muted mt-0.5">{tooltip.col}</p>
          <p
            className={`font-bold mt-1 ${
              tooltip.value === "complete"
                ? "text-status-green"
                : tooltip.value === "overdue"
                  ? "text-status-red"
                  : "text-theme-muted"
            }`}
          >
            {CELL_TITLE[tooltip.value]}
          </p>
          {tooltip.date && (
            <p className="text-theme-muted mt-0.5">{tooltip.date}</p>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 px-1">
        {(["complete", "not_started", "overdue"] as const).map((v) => (
          <div key={v} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm border ${CELL_CLASSES[v]}`} />
            <span className="text-xs text-theme-muted">{CELL_TITLE[v]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
